import React from 'react';
import { DollarSign } from 'lucide-react';
import { Button } from '../../ui/button';

const ROOF_TYPE_LABELS = {
  'landmark': 'Landmark (Baseline Architectural)',
  'landmark_pro': 'Landmark PRO',
  'standing_seam': 'Standing Seam Metal',
  'luxury': 'Luxury Line',
  'asphalt': 'Architectural Shingles',
  'asphalt_shingle': 'Architectural Shingles',
  'metal': 'Metal Roofing',
};

const PricingBreakdown = ({ 
  estimate, 
  formatCurrency,
  onAddCharges,
  onRevertCharges
}) => {
  // Material type used for this estimate
  const roofType = estimate.desired_roof_type || estimate.current_roof_type || 'asphalt';
  const roofTypeLabel = ROOF_TYPE_LABELS[roofType] || roofType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  // Calculate pricing values
  const materialsCost = estimate.pricing?.materials || 0;
  const laborCost = estimate.pricing?.labor || 0;
  const steepRoofCharge = estimate.pricing?.steep_roof_charge || 0;
  const steepRoofApplied = estimate.pricing?.steep_roof_applied || false;
  const salesTaxRate = estimate.pricing?.sales_tax_rate || 0;
  const salesTaxAmount = estimate.pricing?.sales_tax_amount || 0;
  const additionalChargesTotal = estimate.additional_charges?.additional_charges_total || 0;
  const additionalBreakdown = estimate.additional_charges?.breakdown || [];
  const salesTaxOnCharges = estimate.additional_charges?.sales_tax_on_charges || 0;
  const discountPercent = estimate.discount_percent || 0;
  
  const subtotal = materialsCost + laborCost;
  const afterSteepCharge = subtotal + (steepRoofApplied ? steepRoofCharge : 0);
  const afterTax = afterSteepCharge + salesTaxAmount;
  const afterAdditional = afterTax + additionalChargesTotal;
  const discountAmount = discountPercent > 0 ? (afterAdditional * discountPercent / 100) : 0;
  const finalTotal = afterAdditional - discountAmount;
  
  const hasAdditionalCharges = additionalBreakdown.length > 0;
  const originalPrice = subtotal + (steepRoofApplied ? steepRoofCharge : 0);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
        <DollarSign className="w-5 h-5 mr-2 text-blue-600" />
        Pricing Breakdown
      </h2>
      
      {/* Material Type Used */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between" data-testid="material-type-display">
        <div>
          <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">Material</span>
          <p className="text-sm font-semibold text-gray-900">{roofTypeLabel}</p>
        </div>
        <span className="text-xs text-blue-500 bg-blue-100 px-2 py-1 rounded-full font-medium">Used for pricing</span>
      </div>
      
      <div className="space-y-2">
        {/* Base Line Items */}
        <div className="flex justify-between py-2 border-b border-gray-200">
          <span className="text-gray-600">Materials</span>
          <span className="font-medium">{formatCurrency(materialsCost)}</span>
        </div>
        <div className="flex justify-between py-2 border-b border-gray-200">
          <span className="text-gray-600">Labor</span>
          <span className="font-medium">{formatCurrency(laborCost)}</span>
        </div>
        
        {/* Subtotal if there are additional charges */}
        {(steepRoofApplied || hasAdditionalCharges) && (
          <div className="flex justify-between py-2 border-b-2 border-gray-300 bg-gray-50 px-2 rounded">
            <span className="text-gray-700 font-medium">Subtotal</span>
            <span className="font-semibold">{formatCurrency(subtotal)}</span>
          </div>
        )}
        
        {/* Steep Roof Charge */}
        {steepRoofApplied && steepRoofCharge > 0 && (
          <div className="flex justify-between py-2 border-b border-gray-200 bg-amber-50 px-2 rounded">
            <span className="text-gray-700 flex items-center">
              <span className="text-amber-600 mr-2">🏔️</span>
              <span className="font-medium">Steep Roof Charge</span>
              <span className="text-xs text-gray-500 ml-2">
                (Auto-detected: {estimate.satellite_data?.avg_pitch_degrees}°)
              </span>
            </span>
            <span className="font-semibold text-amber-700">+{formatCurrency(steepRoofCharge)}</span>
          </div>
        )}
        
        {/* Sales Tax */}
        {salesTaxRate > 0 && salesTaxAmount > 0 && (
          <div className="flex justify-between py-2 border-b border-gray-200 px-2" data-testid="sales-tax-line">
            <span className="text-gray-600">Sales Tax ({salesTaxRate}%)</span>
            <span className="font-medium">+{formatCurrency(salesTaxAmount)}</span>
          </div>
        )}
        
        {/* Additional Charges */}
        {hasAdditionalCharges && additionalBreakdown.map((charge, index) => (
          <div key={index} className="flex justify-between py-2 border-b border-gray-200 bg-green-50 px-2 rounded">
            <span className="text-gray-700 flex items-center">
              <span className="text-green-600 mr-2">
                {(charge.label || charge.description || '').includes('Stor') ? '🏠' : 
                 (charge.label || charge.description || '').includes('Steep') ? '🏔️' : '💰'}
              </span>
              <span className="font-medium">{charge.label || charge.description}</span>
            </span>
            <span className="font-semibold text-green-700">+{formatCurrency(charge.amount)}</span>
          </div>
        ))}
        
        {/* Sales Tax on Additional Charges */}
        {salesTaxOnCharges > 0 && (
          <div className="flex justify-between py-2 border-b border-gray-200 px-2">
            <span className="text-gray-600">Sales Tax on Additional Charges ({salesTaxRate}%)</span>
            <span className="font-medium">+{formatCurrency(salesTaxOnCharges)}</span>
          </div>
        )}
        
        {/* Discount */}
        {discountPercent > 0 && (
          <>
            <div className="flex justify-between py-2 border-b-2 border-gray-300 bg-gray-50 px-2 rounded mt-2">
              <span className="text-gray-700 font-medium">Subtotal before discount</span>
              <span className="font-semibold">{formatCurrency(afterAdditional)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 bg-orange-50 px-2 rounded">
              <span className="text-orange-700 flex items-center">
                <span className="mr-2">🎟️</span>
                <span className="font-medium">Discount ({discountPercent}%)</span>
              </span>
              <span className="text-orange-700 font-semibold">
                -{formatCurrency(discountAmount)}
              </span>
            </div>
          </>
        )}
        
        {/* Final Total */}
        <div className="flex justify-between py-3 bg-blue-600 text-white px-4 rounded-lg mt-3">
          <span className="text-lg font-bold">Total Estimate</span>
          <span className="text-2xl font-bold">{formatCurrency(finalTotal)}</span>
        </div>
      </div>
      
      {/* All Roof Type Pricing - 2x2 Grid */}
      {estimate.all_roof_pricing && Object.keys(estimate.all_roof_pricing).length > 0 && (
        <div className="mt-6 pt-4 border-t">
          <h3 className="text-base font-semibold text-gray-800 mb-3">Pricing by Roof Type</h3>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(estimate.all_roof_pricing).map(([key, pricing]) => (
              <div key={key} className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
                <p className="text-xs font-medium text-gray-500 mb-1">{pricing.display_name}</p>
                <p className="text-xl font-bold text-blue-700">{formatCurrency(pricing.total)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Additional Charges Summary & Actions */}
      <div className="mt-4 pt-4 border-t">
        {hasAdditionalCharges && (
          <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-orange-800">Additional Charges Applied</span>
              <span className="text-orange-800 font-bold">
                +{formatCurrency(additionalChargesTotal)}
              </span>
            </div>
            <div className="mt-3 pt-3 border-t border-orange-200 flex justify-between items-center">
              <div>
                <div className="text-sm text-gray-500">Original: {formatCurrency(originalPrice)}</div>
                <div className="text-lg font-bold text-gray-900">New Total: {formatCurrency(finalTotal)}</div>
              </div>
              <Button
                onClick={onRevertCharges}
                variant="outline"
                size="sm"
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                Revert to Original
              </Button>
            </div>
          </div>
        )}
        <Button
          onClick={onAddCharges}
          variant="outline"
          className="w-full"
        >
          <DollarSign className="w-4 h-4 mr-2" />
          Add Additional Charges
        </Button>
      </div>
    </div>
  );
};

export default PricingBreakdown;
